import React, { useEffect, useMemo, useRef, useState } from "react";
import { Checkbox, Drawer, Dropdown, Input, Spin, Tooltip, message } from "antd";
import {
  CloseOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FilterOutlined,
  HolderOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import SearchBlueIcon from "../../Assets/reportBuilder/search-blue.svg";
import SaveIcon from "../../Assets/reportBuilder/save-icon-new.svg";
import DownloadIconWhite from "../../Assets/reportBuilder/download-icon-white.svg";
import ChevronDownWhiteIcon from "../../Assets/reportBuilder/chevron-down-white.svg";
import PdfColumnLimitModal, { PDF_COLUMN_LIMIT } from "./PdfColumnLimitModal";
import PreviewTableSkeleton from "./PreviewTableSkeleton";
import {
  formatPreviewDateValue,
  formatReportFieldLabel,
  compareAlphaLabels,
  getReportRowValue,
  matchesReportSearch,
  normalizeReportColumnIds,
  readGenerateReportRows,
} from "./formatFieldLabel";
import { showReportError } from "./reportToast";
import {
  getColumnKey,
  getExportColumnCount,
  getResponseColumnLabel,
  readTotalRows,
  resolveParentExportField,
  displayColumnBelongsToParent,
  parentFieldForDisplayIndex,
  sliceFieldsToExportColumnLimit,
  toApiDisplayColumns,
} from "./reportGenerateUtils";
import GatesPreviewCell, { isGatesPreviewField } from "./GatesPreviewCell";
import SuccessIcon from "../../Assets/campagin/success.svg";
import WarningIcon from "../../Assets/campagin/warning.svg";
import StopIcon from "../../Assets/campagin/stop.svg";
import API_MANAGER from "../../API";
import {
  ensureSocketConnected,
  requestReportDownload,
} from "../../components/Socket/socket";

const FILTER_KEY_OVERRIDES = {
  advert_end_date: "advertEndDate",
  advert_name: "advertId",
  advert_job_title: "experienceJobTitle",
  advert_start_date: "startDate",
  availability: "availabilityDetails",
  industry: "experienceIndustry",
  discipline_auto: "experienceDiscipline",
  city_suburb: "city",
  zip_postal_code: "postalCode",
  drivers_class: "driversLicenseClass",
  drivers_conditions: "driversLicenseConditions",
  drivers_state: "driversLicenseState",
  licence_type_class: "licenceType",
  qualification: "qualificationType",
  medical_assessment: "medicalAssessments",
  job_description_skills: "skills",
  salary_rate: "salary",
  users: "campaignUsers",
  visa_type_number: "visaType",
  auditTrailStatus: "auditTrails",
};

const REPORT_TITLE_PLACEHOLDER = "Enter report name";

const STATE_FILTER_KEYS = [
  "state",
  "state_country",
  "campaign_state",
  "lsl_state",
  "photo_age_state",
];

const CANDIDATE_STATUS_FILTER_KEY = "candidate_status";

const CANDIDATE_STATUS_PAYLOAD_KEYS = [
  "invited",
  "applied",
  "shortlistsOffered",
  "hardReview",
  "roleOffered",
  "roleAccepted",
];

const COMPLIANCE_STATUS_FIELDS = ["hardComplianceStatus", "softComplianceStatus"];

const MULTI_VALUE_FIELDS = [
  "qualificationType",
  "skills",
  "inductions",
  "licenceType",
  "visaType",
  "medicalAssessments",
  "driversLicenseClass",
  "driversLicenseConditions",
  "campaignUsers",
  "soa",
  "voc",
  "qualificationMemberId",
  "qualificationIssueDate",
  "qualificationExpiryDate",
  "qualificationVerificationStatus",
  "vocType",
];

const COMPLIANCE_STATUS_CLASSES = {
  yes: "status-full",
  warning: "status-partial",
  no: "status-none",
};

const getComplianceStatusClass = (fieldId, value) => {
  if (!COMPLIANCE_STATUS_FIELDS.includes(fieldId)) return "";

  const status = String(value ?? "").trim().toLowerCase();
  if (COMPLIANCE_STATUS_CLASSES[status]) return COMPLIANCE_STATUS_CLASSES[status];
  if (status.includes("fully")) return "status-full";
  if (status.includes("partial")) return "status-partial";
  if (status.includes("doesn") || status.includes("not meet")) return "status-none";
  return "";
};

const normalizePreviewFieldKey = (fieldId) =>
  String(fieldId || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");

const isAcceptRejectField = (fieldId) => {
  const key = normalizePreviewFieldKey(fieldId);
  return key === "isapproved" || key.includes("acceptorreject");
};

const getAcceptRejectAction = (row) => {
  const raw = row?.action ?? row?.isApproved ?? row?.Action ?? "";
  const status = String(raw).trim().toLowerCase();
  if (status === "accepted" || status === "accept") return "accepted";
  if (status === "rejected" || status === "reject") return "rejected";
  return "";
};

const getAcceptRejectActionClass = (row, ...fieldIds) => {
  if (!fieldIds.some(isAcceptRejectField)) return "";
  const action = getAcceptRejectAction(row);
  if (action === "accepted") return "action-accepted";
  if (action === "rejected") return "action-rejected";
  return "";
};

const getTrafficLightStatus = (value) => {
  if (value === true) return "yes";
  if (value === false) return "no";
  const status = String(value ?? "").trim().toLowerCase();
  if (status === "yes" || status === "true") return "yes";
  if (status === "no" || status === "false") return "no";
  if (status === "warning") return "warning";
  return null;
};

const renderYesNoTrafficLight = (value) => {
  const status = getTrafficLightStatus(value);
  if (status === "yes") {
    return (
      <Tooltip title="Yes">
        <span className="traffic-light">
          <img src={SuccessIcon} alt="Yes" className="traffic-light-icon" />
        </span>
      </Tooltip>
    );
  }
  if (status === "warning") {
    return (
      <Tooltip title="Warning">
        <span className="traffic-light">
          <img src={WarningIcon} alt="Warning" className="traffic-light-icon" />
        </span>
      </Tooltip>
    );
  }
  if (status === "no") {
    return (
      <Tooltip title="No">
        <span className="traffic-light">
          <img src={StopIcon} alt="No" className="traffic-light-icon" />
        </span>
      </Tooltip>
    );
  }
  return null;
};

const formatDateValue = (value, fieldId) => formatPreviewDateValue(value, fieldId);

const getCellListItems = (fieldId, value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (item && typeof item === "object" ? item.name ?? item.label : item))
      .filter((item) => item !== null && item !== undefined && String(item).trim() !== "")
      .map((item) => formatDateValue(item, fieldId));
  }

  if (!MULTI_VALUE_FIELDS.includes(fieldId) || typeof value !== "string") return [];

  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => formatDateValue(item, fieldId));
};

const renderBulletList = (items) => (
  <ul className="cell-bullet-list">
    {items.map((item, index) => (
      <li key={`${item}-${index}`}>{item}</li>
    ))}
  </ul>
);

const humanizeFilterKey = (key) =>
  String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const extractStateFromLocation = (value) => {
  if (typeof value !== "string") return value;

  const parts = value.split(",").map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 3) return parts[1];
  if (parts.length === 2) return /^\d+$/.test(parts[1]) ? parts[0] : parts[1];
  return value;
};

const FIELD_GROUPS = [
  {
    category: "Candidate Info",
    fields: [
      { id: "firstName", label: "First Name", badge: "Worker" },
      { id: "lastName", label: "Last Name", badge: "Worker" },
      { id: "candidateName", label: "Full Name", badge: "Worker" },
      { id: "email", label: "Email", badge: "Worker" },
      { id: "gender", label: "Gender", badge: "Worker" },
      { id: "ethnicity", label: "Ethnicity", badge: "Worker" },
      { id: "discipline", label: "Discipline", badge: "Worker" },
      { id: "availabilityDetails", label: "Notice Period", badge: "Worker" },
      { id: "isAvailable", label: "Availability", badge: "Worker" },
      { id: "street", label: "Street", badge: "Worker" },
      { id: "city", label: "Suburb", badge: "Worker" },
      { id: "postalCode", label: "Postal Code", badge: "Worker" },
      { id: "state", label: "State", badge: "Worker" },
      { id: "country", label: "Country", badge: "Worker" },
      { id: "dob", label: "DOB", badge: "Worker" },
      { id: "phoneNumber", label: "Phone Number", badge: "Worker" },
      { id: "fullAddress", label: "Full Address", badge: "Worker" },
      { id: "nextOfKinName", label: "Next-of-Kin", badge: "Worker" },
      { id: "usiNumber", label: "USI Number", badge: "Worker" },
    ],
  },
  {
    category: "Experience",
    fields: [
      { id: "employer", label: "Employer", badge: "Job History" },
      { id: "projectName", label: "Project Name",badge: "Job History"},
      { id: "experienceJobTitle", label: "Job Title", badge: "Job History" },
      { id: "experienceIndustry", label: "Industry", badge: "Job History" },
      { id: "experienceDiscipline", label: "Discipline", badge: "Job History" },
      { id: "inductions", label: "Inductions", badge: "Job History" },
      { id: "skills", label: "Skills", badge: "Job History" },
      { id: "uploadResume", label: "CV Uploaded"},
      { id: "profileSummaryDescription", label: "Profile Summary", badge: "Job History" },
      { id: "experienceStartDate", label: "Start Date", badge: "Job History" },
      { id: "experienceEndDate", label: "End Date", badge: "Job History" },
      { id: "refereeName", label: "Referee", badge: "Job History" },
      { id: "introVideo", label: "Intro Video Uploaded", badge: "Job History" },
    ],
  },
  {
    category: "Qualifications & ID Documents",
    fields: [
      
    
      // { id: "licenceType", label: "Licence Type", badge: "Qualification" },
    

      { id: "uploadQualification", label: "Qualification"},
      { id: "soa", label: "Qualification SOA"},
      { id: "voc", label: "Qualification VOC", },
   
    
      { id: "driversLicenseClass", label: "Driver Licence"},
    
      
   
      
      { id: "passportExpiryDate", label: "Passport"},
    
    
      { id: "photoAgeExpiryDate", label: "Photo Age" },
     
    ],
  },
  {
    category: "Working rights",
    fields: [
      { id: "citizenship", label: "Citizenship", badge: "Worker" },
    
      { id: "visaUpload", label: "Visa"},
      { id: "policeClearanceUploaded", label: "Police Clearance" },
  
    ],
  },
  {
    category: "Other",
    fields: [
     
      { id: "lslState", label: "LSL", badge: "Worker"},
      { id: "travelPreferredAirline", label: "Preferred Airline & FFN"},
      // { id: "travelFrequentFlyerNumber", label: "Frequent Flyer No.", badge: "Candidate" },
     
      { id: "ppeBootsSize", label: "PPE", badge: "Worker" },
      { id: "medicalAssessments", label: "Medical" },
  
      // { id: "superannuationFundName", label: "Fund Name", badge: "Superannuation" },
      // { id: "superannuationFundUsiAbn", label: "Fund USI/ABN", badge: "Superannuation" },
      // { id: "superannuationMemberNumber", label: "Member Number", badge: "Superannuation" },
    ],
  },
  {
    category: "Advert information",
    fields: [
      { id: "campaignName", label: "Campaign / Project Name",},
      { id: "advertId", label: "Advert Name / Job Title",},
      { id: "projectCode", label: "Advert Project Code",},
      { id: "campaignIndustry", label: "Advert Industry",},
      { id: "campaignSuburb", label: "Advert Suburb",},
      { id: "campaignState", label: "Advert State",},
      { id: "campaignPostalCode", label: "Advert Postal Code",},
      { id: "campaignUsers", label: "Advert Users",},
      { id: "salary", label: "Advert Rate/Salary",},
      { id: "roster", label: "Advert Roster",},
      { id: "hours", label: "Advert Weekly Hours",},
      { id: "jobDuration", label: "Advert Job Duration",},
      { id: "employmentType", label: "Advert Employment Type",},
      { id: "advertDiscipline", label: "Advert Discipline", },
      { id: "targetHeadcount", label: "Advert Target Headcount",},
      { id: "campaignTargetHeadcount", label: "Campaign Target Headcount",},
      { id: "startDate", label: "Advert Start Date",},
      { id: "advertEndDate", label: "Advert End Date",},
      { id: "jobDescription", label: "Advert Description",},
    ],
  },
  {
    category: "Compliance & Status",
    fields: [
      // { id: "hardRequirements", label: "Hard Requirements", badge: "Compliance" },
      // { id: "softRequirements", label: "Soft Requirements", badge: "Compliance" },
      { id: "hardComplianceStatus", label: "Hard Tracker Status", badge: "Advert" },
      { id: "softComplianceStatus", label: "Soft Tracker Status", badge: "Advert" },
      { id: "notes", label: "Candidate Notes", badge: "Advert" },
    ],
  },
  {
    category: "Approval & Status",
    fields: [
      { id: "invited", label: "Invited", badge: "Status" },
      { id: "applied", label: "Applied", badge: "Status" },
      { id: "shortlistsOffered", label: "Shortlists Offered", badge: "Status" },
      { id: "hardReview", label: "Hard Review", badge: "Status" },
      { id: "roleOffered", label: "Role Offered", badge: "Status" },
      { id: "roleAccepted", label: "Role Accepted", badge: "Status" },
    
    ],
  },
  {
    category: "User Actions",
    fields: [
      { id: "isApproved", label: "Accept or Reject ", badge: "Actions" },
      { id: "auditTrails", label: "Audit Trails"},
      // { id: "isApprovedByUser", label: "Action by user", badge: "Advert Status" },
    ],
  },
  {
    category: "Account User List",
    fields: [
      { id: "userName", label: "User Account Name" },
      { id: "userType", label: "User Account Permission",},
      { id: "userStatus", label: "User Account Status",},
    ],
  },
  // {
  //   category: "Mobilisation Management",
  //   fields: [
  //     // { id: "gates", label: "Gates", },
  //   ],
  // },
].map((group) => ({
  ...group,
  fields: [...group.fields]
    .map((field) => ({ ...field, label: formatReportFieldLabel(field) }))
    .sort((a, b) => compareAlphaLabels(a.label, b.label)),
}));


const ALL_PREVIEW_FIELDS = FIELD_GROUPS.flatMap((group) => group.fields);

const getFieldLabel = (fieldId) => {
  const key = getColumnKey(fieldId);
  return ALL_PREVIEW_FIELDS.find((field) => field.id === key)?.label || getResponseColumnLabel(fieldId);
};

function PreviewReport({
  reportTitle,
  previewData,
  selectedFields,
  apiColumns,
  filters,
  filterPayload,
  filterSummary,
  totalRows,
  loadOnMount = false,
  onTitleChange,
  reportId,
  onSaved,
  onPreviewUpdate,
}) {
  const [exportLoading, setExportLoading] = useState(false);
  const exportWaitRef = useRef(false);

  useEffect(() => {
    ensureSocketConnected();

    const clearExportWait = () => {
      exportWaitRef.current = false;
      setExportLoading(false);
    };

    window.addEventListener("cinchy-report-export-downloaded", clearExportWait);
    window.addEventListener("cinchy-report-export-failed", clearExportWait);
    return () => {
      window.removeEventListener("cinchy-report-export-downloaded", clearExportWait);
      window.removeEventListener("cinchy-report-export-failed", clearExportWait);
    };
  }, []);
  const [saveLoading, setSaveLoading] = useState(false);
  const [pdfLimitModal, setPdfLimitModal] = useState(false);
  const [columns, setColumns] = useState(() =>
    normalizeReportColumnIds(selectedFields || [], ALL_PREVIEW_FIELDS)
  );
  const [displayColumns, setDisplayColumns] = useState(() =>
    toApiDisplayColumns(apiColumns)
  );
  const [title, setTitle] = useState(reportTitle || "Untitled report");
  const [rows, setRows] = useState(() =>
    Array.isArray(previewData) ? previewData : []
  );
  const [rowCount, setRowCount] = useState(
    typeof totalRows === "number" ? totalRows : previewData?.length || 0
  );
  const [rowsLoading, setRowsLoading] = useState(Boolean(loadOnMount));
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [arrangeOpen, setArrangeOpen] = useState(false);
  const [arrangeDragIndex, setArrangeDragIndex] = useState(null);
  const [arrangeDropIndex, setArrangeDropIndex] = useState(null);
  const [fieldSearch, setFieldSearch] = useState("");
  const refreshTimer = useRef(null);
  const columnsRef = useRef(
    normalizeReportColumnIds(selectedFields || [], ALL_PREVIEW_FIELDS)
  );
  const displayColumnsRef = useRef(displayColumns);
  const rowsRef = useRef(rows);
  const rowCountRef = useRef(rowCount);
  const onPreviewUpdateRef = useRef(onPreviewUpdate);
  const arrangeDragIndexRef = useRef(null);
  const arrangeDropIndexRef = useRef(null);
  const arrangeOrderOnOpenRef = useRef("");
  const previewRequestId = useRef(0);
  const reportName = title.trim() || "Untitled report";
  const tableColumns = useMemo(
    () => toApiDisplayColumns(displayColumns.length ? displayColumns : apiColumns),
    [displayColumns, apiColumns]
  );

  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  useEffect(() => {
    displayColumnsRef.current = displayColumns;
  }, [displayColumns]);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    rowCountRef.current = rowCount;
  }, [rowCount]);

  useEffect(() => {
    onPreviewUpdateRef.current = onPreviewUpdate;
  }, [onPreviewUpdate]);

  useEffect(
    () => () => {
      clearTimeout(refreshTimer.current);
      previewRequestId.current += 1;
      if (onPreviewUpdateRef.current) {
        onPreviewUpdateRef.current(
          rowsRef.current,
          normalizeReportColumnIds(columnsRef.current, ALL_PREVIEW_FIELDS),
          rowCountRef.current,
          displayColumnsRef.current
        );
      }
    },
    []
  );

  const handleTitleChange = (value) => {
    setTitle(value);
    if (onTitleChange) onTitleChange(value);
  };

  const convertKeyToCamelCase = (key) => {
    if (FILTER_KEY_OVERRIDES[key]) {
      return FILTER_KEY_OVERRIDES[key];
    }
    return key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  };

  const parseDateRange = (value) => {
    if (typeof value !== "string" || !value.startsWith("{")) return null;
    try {
      const parsed = JSON.parse(value);
      return parsed?.startDate && parsed?.endDate
        ? [parsed.startDate, parsed.endDate]
        : null;
    } catch (e) {
      return null;
    }
  };

  const buildFilters = () =>
    (filters || [])
      .filter((f) => f.key && (Array.isArray(f.value) ? f.value.length > 0 : f.value))
      .flatMap((f) => {
        if (f.key === CANDIDATE_STATUS_FILTER_KEY) {
          const statuses = Array.isArray(f.value) ? f.value : [f.value];
          return statuses
            .filter((status) => CANDIDATE_STATUS_PAYLOAD_KEYS.includes(status))
            .map((status) => ({
              key: status,
              operator: f.operator || "contains",
              value: ["yes"],
              labels: ["yes"],
            }));
        }

        if (f.key === "salary_rate") {
          const seen = new Set();
          const salaryValues = (Array.isArray(f.value) ? f.value : [f.value])
            .flatMap((item) =>
              String(item ?? "")
                .replace(/\$/g, "")
                .split("-")
                .map((part) => part.trim())
                .filter(Boolean)
            )
            .filter((part) => {
              if (seen.has(part)) return false;
              seen.add(part);
              return true;
            })
            .sort((a, b) => {
              const aNum = Number(a);
              const bNum = Number(b);
              if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
              return a.localeCompare(b, undefined, { numeric: true });
            });
          return {
            key: convertKeyToCamelCase(f.key),
            operator: f.operator || "contains",
            value: salaryValues,
            labels: salaryValues,
          };
        }

        const dateRange = parseDateRange(f.value);
        let valueArray;

        if (dateRange) {
          valueArray = dateRange;
        } else {
          const list = Array.isArray(f.value) ? f.value : [f.value];
          valueArray = list.flatMap((item) => {
            if (item === undefined || item === null || item === "") return [];
            if (typeof item !== "string") return [item];
            return item.split(",").map((part) => part.trim()).filter(Boolean);
          });
          if (STATE_FILTER_KEYS.includes(f.key)) {
            valueArray = valueArray.map(extractStateFromLocation);
          }
          valueArray = valueArray.map((v) => (typeof v === "string" ? v.toLowerCase() : v));
          if (f.key === "auditTrailStatus" || f.key === "auditTrails") {
            valueArray = valueArray.map((v) => {
              if (typeof v !== "string") return v;
              const compact = v.toLowerCase().replace(/\s+/g, "");
              const legacy = {
                shortlistsoffered: "shortlists offered",
                hardreview: "hard review",
                roleoffered: "role offered",
                roleaccepted: "role accepted",
              };
              return legacy[compact] || v;
            });
          }
        }

        return {
          key: convertKeyToCamelCase(f.key),
          operator: f.operator || "contains",
          value: valueArray,
          labels: valueArray,
        };
      });

  // the builder already resolved dropdown ids to names, so reuse its payload
  const getFilterPayload = () =>
    Array.isArray(filterPayload) ? filterPayload : buildFilters();

  const toColumnPayload = (nextColumns) =>
    normalizeReportColumnIds(nextColumns, ALL_PREVIEW_FIELDS);

  const applyColumnOrder = (nextList, { remapDisplay = true } = {}) => {
    const next = toColumnPayload(nextList);
    columnsRef.current = next;
    setColumns(next);

    if (!remapDisplay) return next;

    schedulePreviewRefresh(next);

    if (onPreviewUpdateRef.current) {
      onPreviewUpdateRef.current(
        rowsRef.current,
        next,
        rowCountRef.current,
        displayColumnsRef.current
      );
    }
    return next;
  };

  const handleArrangeDragStart = (e, index) => {
    arrangeDragIndexRef.current = index;
    setArrangeDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const setArrangeDropIndexIfChanged = (next) => {
    if (arrangeDropIndexRef.current === next) return;
    arrangeDropIndexRef.current = next;
    setArrangeDropIndex(next);
  };

  const commitArrangeOrder = (insertAt) => {
    const from = arrangeDragIndexRef.current;
    if (from === null || insertAt === null || insertAt === undefined) return;
    const list = [...columnsRef.current];
    if (from < 0 || from >= list.length) return;
    let to = insertAt;
    if (to > from) to -= 1;
    if (to === from || to < 0 || to > list.length) return;
    const [item] = list.splice(from, 1);
    list.splice(to, 0, item);
    applyColumnOrder(list);
  };

  const handleArrangeDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (arrangeDragIndexRef.current === null) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const insertAfter = e.clientY > rect.top + rect.height / 2;
    setArrangeDropIndexIfChanged(insertAfter ? index + 1 : index);
  };

  const resetArrangeDrag = () => {
    arrangeDragIndexRef.current = null;
    arrangeDropIndexRef.current = null;
    setArrangeDragIndex(null);
    setArrangeDropIndex(null);
  };

  const handleArrangeDrop = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const insertAfter = e.clientY > rect.top + rect.height / 2;
    commitArrangeOrder(insertAfter ? index + 1 : index);
    resetArrangeDrag();
  };

  const handleArrangeDragEnd = () => {
    resetArrangeDrag();
  };

  const getArrangeDropClass = (index) => {
    if (arrangeDropIndex === null || arrangeDragIndex === null) return "";
    if (arrangeDropIndex === arrangeDragIndex || arrangeDropIndex === arrangeDragIndex + 1) {
      return "";
    }
    if (arrangeDropIndex === index) return "drop-before";
    if (
      arrangeDropIndex === columns.length &&
      index === columns.length - 1
    ) {
      return "drop-after";
    }
    return "";
  };

  const refreshPreview = async (nextColumns) => {
    const columnIds = toColumnPayload(nextColumns);
    const requestId = ++previewRequestId.current;

    if (columnIds.length === 0) {
      setRows([]);
      setDisplayColumns([]);
      setRowCount(0);
      setRowsLoading(false);
      if (onPreviewUpdate) onPreviewUpdate([], columnIds, 0, []);
      return;
    }

    const payload = {
      format: "preview",
      name: reportName,
      columns: columnIds,
    };
    const formattedFilters = getFilterPayload();
    if (formattedFilters.length > 0) {
      payload.filters = formattedFilters;
    }

    setRowsLoading(true);
    try {
      const response = await API_MANAGER.generateReport(payload);
      if (requestId !== previewRequestId.current) return;

      const data = response?.data?.data;
      const nextRows = readGenerateReportRows(response);
      const nextDisplay = toApiDisplayColumns(data?.cols);
      const nextTotal = readTotalRows(data, nextRows.length);
      rowsRef.current = nextRows;
      displayColumnsRef.current = nextDisplay;
      rowCountRef.current = nextTotal;
      setRows(nextRows);
      setDisplayColumns(nextDisplay);
      setRowCount(nextTotal);
      if (onPreviewUpdate) {
        onPreviewUpdate(nextRows, columnIds, nextTotal, nextDisplay);
      }
    } catch (error) {
      if (requestId !== previewRequestId.current) return;
      showReportError(
        error,
        rowsRef.current.length === 0 ? "Failed to load preview" : "Failed to refresh preview"
      );
    }
    if (requestId === previewRequestId.current) {
      setRowsLoading(false);
    }
  };

  useEffect(() => {
    if (!loadOnMount) return;
    refreshPreview(columnsRef.current);
    // Fetch once when the preview screen opens. Later column edits refresh themselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const schedulePreviewRefresh = (nextColumns) => {
    clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => refreshPreview(nextColumns), 600);
  };

  const toggleColumn = (fieldId) => {
    if (!fieldId) return;
    const currentColumns = toColumnPayload(columnsRef.current);
    const parentId = resolveParentExportField(fieldId, currentColumns, ALL_PREVIEW_FIELDS);
    const isRemoving = currentColumns.includes(parentId);
    const nextColumns = isRemoving
      ? currentColumns.filter((id) => id !== parentId)
      : [...currentColumns, parentId];

    columnsRef.current = nextColumns;
    setColumns(nextColumns);

    if (isRemoving) {
      setDisplayColumns((prev) =>
        prev.filter((col) => !displayColumnBelongsToParent(col, parentId, ALL_PREVIEW_FIELDS))
      );
    }

    schedulePreviewRefresh(nextColumns);
  };

  const removePreviewColumn = (col, index) => {
    const currentColumns = toColumnPayload(columnsRef.current);
    const mappedId = toColumnPayload([getColumnKey(col)])[0];
    const parentId =
      (currentColumns.includes(
        resolveParentExportField(col, currentColumns, ALL_PREVIEW_FIELDS)
      ) &&
        resolveParentExportField(col, currentColumns, ALL_PREVIEW_FIELDS)) ||
      (currentColumns.includes(
        resolveParentExportField(mappedId, currentColumns, ALL_PREVIEW_FIELDS)
      ) &&
        resolveParentExportField(mappedId, currentColumns, ALL_PREVIEW_FIELDS)) ||
      parentFieldForDisplayIndex(currentColumns, index) ||
      (currentColumns.includes(mappedId) ? mappedId : "") ||
      currentColumns[index] ||
      "";

    if (!parentId || !currentColumns.includes(parentId)) return;

    const nextColumns = currentColumns.filter((id) => id !== parentId);
    columnsRef.current = nextColumns;
    setColumns(nextColumns);
    setDisplayColumns((prev) =>
      prev.filter((displayCol, displayIndex) => {
        if (displayColumnBelongsToParent(displayCol, parentId, ALL_PREVIEW_FIELDS)) {
          return false;
        }
        return parentFieldForDisplayIndex(currentColumns, displayIndex) !== parentId;
      })
    );
    schedulePreviewRefresh(nextColumns);
  };

  const toggleGroupColumns = (groupFields, selectAll) => {
    const currentColumns = toColumnPayload(columnsRef.current);
    const groupIds = groupFields.map((field) => field.id);
    const nextColumns = selectAll
      ? [...currentColumns, ...groupIds.filter((id) => !currentColumns.includes(id))]
      : currentColumns.filter((id) => !groupIds.includes(id));

    columnsRef.current = nextColumns;
    setColumns(nextColumns);
    schedulePreviewRefresh(nextColumns);
  };

  const filteredGroups = useMemo(() => {
    const query = fieldSearch.trim().toLowerCase();
    if (!query) return FIELD_GROUPS;

    return FIELD_GROUPS.map((group) => ({
      ...group,
      fields: group.fields.filter((field) =>
        matchesReportSearch(fieldSearch, field.label, group.category, field.badge)
      ),
    })).filter((group) => group.fields.length > 0);
  }, [fieldSearch]);

  const handleSave = async () => {
    if (columns.length === 0) {
      message.warning("Please select at least one column");
      return;
    }

    const formattedFilters = getFilterPayload();
    const payload = {
      name: reportName,
      cols: toColumnPayload(columns),
      filters: formattedFilters,
    };

    setSaveLoading(true);
    try {
      if (reportId) {
        await API_MANAGER.updateReport(reportId, payload);
        message.success("Report successfully updated.");
      } else {
        const response = await API_MANAGER.saveReport(payload);
        const body = response?.data?.data;
        const saved = body?.data || body?.report || body;
        if (onSaved) onSaved(saved?._id || saved?.id);
        message.success("Report successfully saved.");
      }
    } catch (error) {
      showReportError(error, "Failed to save report");
    }
    setSaveLoading(false);
  };

  const handleExport = async (doctype, columnsOverride = null) => {
    if (columns.length === 0) {
      message.warning("Please select at least one column");
      return;
    }

    const formattedFilters = getFilterPayload();

    const payload = {
      format: "export",
      name: reportName || "Untitled report",
      columns: toColumnPayload(columnsOverride || columns),
      doctype,
    };

    if (formattedFilters.length > 0) {
      payload.filters = formattedFilters;
    }

    if (exportWaitRef.current) return;

    setExportLoading(doctype);
    exportWaitRef.current = true;

    try {
      await requestReportDownload(() => API_MANAGER.generateReport(payload), {
        fileType: doctype,
        onResponse: (_response, meta) => {
          exportWaitRef.current = false;
          setExportLoading(false);
          if (meta?.background) {
            message.success(
              "Your export is being processed and will download automatically once ready."
            );
          }
        },
      });
    } catch (error) {
      if (error?.response) {
        showReportError(error, "Failed to export report");
      } else {
        message.error(error?.message || "Could not connect. Please try the download again.");
      }
    } finally {
      exportWaitRef.current = false;
      setExportLoading(false);
    }
  };

  const formatCellValue = (value, fieldId) => {
    if (value === null || value === undefined) return "-";

    if (Array.isArray(value)) {
      const joined = value
        .map((item) => (item && typeof item === "object" ? item.name : item))
        .filter((item) => item !== null && item !== undefined && String(item).trim() !== "")
        .map((item) => formatDateValue(item, fieldId))
        .join(", ");
      return joined || "-";
    }

    if (typeof value === "object") {
      return formatDateValue(String(value.name || "").trim(), fieldId) || "-";
    }

    return String(value).trim() === "" ? "-" : formatDateValue(value, fieldId);
  };

  const renderPreviewCell = (fieldId, value) => {
    if (isGatesPreviewField(fieldId)) {
      return <GatesPreviewCell value={value} />;
    }
    if (!COMPLIANCE_STATUS_FIELDS.includes(fieldId)) {
      const trafficLight = renderYesNoTrafficLight(value);
      if (trafficLight) return trafficLight;
    }
    const items = getCellListItems(fieldId, value);
    if (items.length >= 1) return renderBulletList(items);
    return formatCellValue(value, fieldId);
  };

  const requestPdfExport = () => {
    if (columns.length === 0) {
      message.warning("Please select at least one column");
      return;
    }
    if (getExportColumnCount(columns) > PDF_COLUMN_LIMIT) {
      setPdfLimitModal("pdf");
      return;
    }
    handleExport("pdf");
  };

  const appliedFilters = useMemo(() => {
    if (Array.isArray(filterSummary) && filterSummary.length > 0) {
      return filterSummary.filter((item) => item?.values?.length > 0);
    }

    return (filters || [])
      .filter((f) => f.key && (Array.isArray(f.value) ? f.value.length > 0 : f.value))
      .map((f) => {
        const dateRange = parseDateRange(f.value);
        return {
          key: f.key,
          keyLabel: humanizeFilterKey(f.key),
          operatorLabel: dateRange
            ? "Between"
            : f.operator === "notContains"
            ? "Not Contain"
            : "Contains",
          values: dateRange
            ? [`${dateRange[0]} - ${dateRange[1]}`]
            : Array.isArray(f.value)
            ? f.value
            : [f.value],
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSummary, filters]);

  const exportMenuItems = [
    {
      key: "pdf",
      label: (
        <div className="export-menu-item">
          <FilePdfOutlined className="export-menu-icon pdf" />
          <span>Export as PDF</span>
        </div>
      ),
      onClick: requestPdfExport,
    },
    {
      key: "csv",
      label: (
        <div className="export-menu-item">
          <FileTextOutlined className="export-menu-icon csv" />
          <span>Export as CSV</span>
        </div>
      ),
      onClick: () => handleExport("xlsx"),
    },
  ];

  return (
    <div className="preview-report-page">
      <div className="preview-report-header">
        <div className="preview-title-block">
          <div className="preview-title-row">
            <Tooltip title="Click to rename" placement="top">
              <span className="preview-title-input-wrap">
                <Input
                  className="preview-title-input"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  onFocus={(e) => {
                    if (title.trim() === "Untitled report") {
                      handleTitleChange("");
                      return;
                    }
                    e.target.select();
                  }}
                  bordered={false}
                  placeholder={REPORT_TITLE_PLACEHOLDER}
                />
              </span>
            </Tooltip>
          </div>
          <p className="preview-subtitle">
            Review the generated report and export it as PDF or Excel.
          </p>
        </div>
        <div className="preview-actions">
          <button
            type="button"
            className="arrange-column-btn"
            onClick={() => {
              arrangeOrderOnOpenRef.current = toColumnPayload(columnsRef.current).join("|");
              setArrangeOpen(true);
            }}
          >
            <UnorderedListOutlined />
            <span>Rearrange Columns</span>
          </button>
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            overlayClassName="applied-filters-dropdown"
            dropdownRender={() => (
              <div className="applied-filters-panel">
                <div className="applied-filters-head">
                  <span className="applied-filters-heading">Applied Filters</span>
                  <span className="applied-filters-total">
                    {appliedFilters.length}
                  </span>
                </div>
                {appliedFilters.length > 0 ? (
                  <div className="applied-filters-list">
                    {appliedFilters.map((filter, index) => (
                      <div
                        key={`${filter.key}-${index}`}
                        className="applied-filter-item"
                      >
                        <div className="applied-filter-meta">
                          <span className="applied-filter-key">{filter.keyLabel}</span>
                          {filter.operatorLabel && (
                            <span className="applied-filter-operator">
                              {filter.operatorLabel}
                            </span>
                          )}
                        </div>
                        <div className="applied-filter-values">
                          {filter.values.map((value, valueIndex) => (
                            <span
                              key={`${value}-${valueIndex}`}
                              className="applied-filter-value"
                              title={String(value)}
                            >
                              {value}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="applied-filters-empty">
                    No filters were applied to this report.
                  </p>
                )}
              </div>
            )}
          >
            <button type="button" className="applied-filters-btn">
              <FilterOutlined />
              <span>Filters</span>
              {appliedFilters.length > 0 && (
                <span className="applied-filters-count">{appliedFilters.length}</span>
              )}
            </button>
          </Dropdown>
          <button
            type="button"
            className="add-column-btn"
            onClick={() => setFieldsOpen(true)}
          >
            <PlusOutlined />
            <span>Add Column</span>
          </button>
          <button
            type="button"
            className="save-btn"
            onClick={handleSave}
            disabled={saveLoading}
          >
            <img src={SaveIcon} alt="" />
            <span>
              {saveLoading ? "Saving..." : reportId ? "Update Report" : "Save Report"}
            </span>
          </button>
          <Dropdown
            menu={{ items: exportMenuItems }}
            trigger={["click"]}
            placement="bottomRight"
            disabled={exportLoading}
            overlayClassName="export-dropdown-menu"
          >
            <button type="button" className="export-btn" disabled={exportLoading}>
              <img src={DownloadIconWhite} alt="" />
              <span>{exportLoading ? "Exporting..." : "Export"}</span>
              <img src={ChevronDownWhiteIcon} alt="" className="chevron-icon" />
            </button>
          </Dropdown>
        </div>
      </div>

      <div className="preview-report-table">
        <div
          className="preview-table-scroll"
          style={{ "--column-count": (tableColumns.length || columns.length) || 1 }}
        >
          <div className="preview-table-header">
            {(tableColumns.length ? tableColumns : columns).map((col, index) => {
              const headerLabel =
                typeof col === "object"
                  ? col.label || getFieldLabel(col.key)
                  : getFieldLabel(col);
              return (
              <div
                key={`${headerLabel}-${index}`}
                className="header-column"
              >
                <span className="header-label">{headerLabel}</span>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removePreviewColumn(col, index)}
                >
                  <CloseOutlined />
                </button>
              </div>
              );
            })}
          </div>

          <div className="preview-table-body">
            {rowsLoading ? (
              <PreviewTableSkeleton
                columns={(tableColumns.length ? tableColumns : columns).length}
              />
            ) : rows.length > 0 ? (
              rows.map((row, rowIndex) => (
                <div key={rowIndex} className="preview-table-row">
                  {(tableColumns.length ? tableColumns : columns).map((col, index) => {
                    const colKey = getColumnKey(col);
                    const fieldId =
                      tableColumns.length === columns.length
                        ? getColumnKey(columns[index])
                        : toColumnPayload([colKey])[0] || colKey;
                    const cellValue =
                      row?.[colKey] ??
                      row?.[col?.label] ??
                      getReportRowValue(row, fieldId, ALL_PREVIEW_FIELDS);
                    return (
                    <div
                      key={`${colKey}-${index}`}
                      className={`cell-text ${
                        isGatesPreviewField(fieldId) || isGatesPreviewField(colKey)
                          ? "cell-gates"
                          : ""
                      } ${getComplianceStatusClass(
                        fieldId,
                        cellValue
                      )} ${getAcceptRejectActionClass(
                        row,
                        fieldId,
                        colKey
                      )}`.trim()}
                    >
                      {renderPreviewCell(fieldId, cellValue)}
                    </div>
                    );
                  })}
                </div>
              ))
            ) : (
              <div className="preview-empty">No data found for the selected filters</div>
            )}
          </div>
        </div>
      </div>

      {!rowsLoading && rows.length > 0 && (
        <div className="preview-table-footer">
          <span className="preview-row-count">
            {rowCount > rows.length
              ? `Showing ${rows.length} of ${rowCount} rows`
              : `Showing ${rows.length} ${rows.length === 1 ? "row" : "rows"}`}
          </span>
          {rowCount > rows.length && (
            <span className="preview-row-hint">
              Export the report to get all {rowCount} rows.
            </span>
          )}
        </div>
      )}

      <Drawer
        title="Rearrange Columns"
        placement="right"
        width={380}
        open={arrangeOpen}
        onClose={() => {
          setArrangeOpen(false);
          const nextOrder = toColumnPayload(columnsRef.current).join("|");
          if (onPreviewUpdateRef.current) {
            onPreviewUpdateRef.current(
              rowsRef.current,
              normalizeReportColumnIds(columnsRef.current, ALL_PREVIEW_FIELDS),
              rowCountRef.current,
              displayColumnsRef.current
            );
          }
          if (nextOrder && nextOrder !== arrangeOrderOnOpenRef.current) {
            message.success("Column rearranged saved");
          }
        }}
        className="report-fields-drawer arrange-columns-drawer"
      >
        <p className="drawer-subtitle">
          These are the columns selected for this report. Drag to change their order.
        </p>
        <div
          className="arrange-columns-list"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            commitArrangeOrder(arrangeDropIndexRef.current);
            resetArrangeDrag();
          }}
        >
          {columns.map((fieldId, index) => {
            const headerLabel = getFieldLabel(fieldId);
            return (
              <div
                key={getColumnKey(fieldId) || `column-${index}`}
                className={`arrange-column-item ${
                  arrangeDragIndex === index ? "dragging" : ""
                } ${getArrangeDropClass(index)}`}
                draggable
                onDragStart={(e) => handleArrangeDragStart(e, index)}
                onDragOver={(e) => handleArrangeDragOver(e, index)}
                onDrop={(e) => handleArrangeDrop(e, index)}
                onDragEnd={handleArrangeDragEnd}
              >
                <HolderOutlined className="drag-icon" />
                <span className="header-label" title={headerLabel}>
                  {headerLabel}
                </span>
                <button
                  type="button"
                  className="remove-btn"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => removePreviewColumn(fieldId, index)}
                >
                  <CloseOutlined />
                </button>
              </div>
            );
          })}
          {columns.length === 0 && (
            <p className="fields-empty">No columns in this report.</p>
          )}
        </div>
      </Drawer>

      <Drawer
        title="Available Fields"
        placement="right"
        width={380}
        open={fieldsOpen}
        onClose={() => setFieldsOpen(false)}
        className="report-fields-drawer"
      >
        <p className="drawer-subtitle">
          Select the columns you want in this report. The preview updates automatically.
        </p>
        <Input
          className="fields-search"
          placeholder="Search fields"
          prefix={<img src={SearchBlueIcon} alt="" className="fields-search-icon" />}
          value={fieldSearch}
          onChange={(e) => setFieldSearch(e.target.value)}
          allowClear
        />

        <div className="fields-list">
          {filteredGroups.map((group) => {
            const columnKeys = columns.map(getColumnKey);
            const groupSelectedCount = group.fields.filter((field) =>
              columnKeys.includes(field.id)
            ).length;
            const allGroupSelected = groupSelectedCount === group.fields.length;

            return (
            <div className="field-group" key={group.category}>
              <div className="field-group-header">
                <p className="field-group-title">{group.category}</p>
                <Checkbox
                  className="field-group-select-all"
                  checked={allGroupSelected}
                  indeterminate={groupSelectedCount > 0 && !allGroupSelected}
                  onChange={(e) => toggleGroupColumns(group.fields, e.target.checked)}
                >
                  Select all
                </Checkbox>
              </div>
              <div className="field-group-items">
                {group.fields.map((field) => {
                  const checked = columnKeys.includes(field.id);
                  return (
                    <button
                      type="button"
                      key={field.id}
                      className={`field-item ${checked ? "selected" : ""}`}
                      onClick={() => toggleColumn(field.id)}
                    >
                      <div className="field-item-left">
                        <span className="field-label" title={field.label}>{field.label}</span>
                      </div>
                      <Checkbox
                        checked={checked}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleColumn(field.id)}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
            );
          })}
          {filteredGroups.length === 0 && (
            <p className="fields-empty">No fields match your search.</p>
          )}
        </div>
      </Drawer>

      <PdfColumnLimitModal
        open={Boolean(pdfLimitModal)}
        showColumnLimitNotice={
          pdfLimitModal === "pdf" &&
          getExportColumnCount(columns) > PDF_COLUMN_LIMIT
        }
        showExcelOption={pdfLimitModal === "pdf"}
        onCancel={() => setPdfLimitModal(false)}
        onDownloadCsv={() => {
          setPdfLimitModal(false);
          handleExport("xlsx");
        }}
        onProceed={() => {
          const doctype = pdfLimitModal === "xlsx" ? "xlsx" : "pdf";
          const overPdfLimit =
            doctype === "pdf" && getExportColumnCount(columns) > PDF_COLUMN_LIMIT;
          setPdfLimitModal(false);
          handleExport(
            doctype,
            overPdfLimit
              ? sliceFieldsToExportColumnLimit(columns, PDF_COLUMN_LIMIT)
              : null
          );
        }}
      />
      {(exportLoading === "xlsx" || exportLoading === "pdf") && (
        <div className="report-export-loader" role="status" aria-live="polite">
          <Spin size="large" />
          <p>
            {exportLoading === "pdf"
              ? "Preparing your PDF file"
              : "Preparing your Excel file"}
          </p>
        </div>
      )}
    </div>
  );
}

export default PreviewReport;
